import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  FormProvider,
  type FieldPath,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import {
  GuaranteesMainDealConditionsFieldName,
  type TGuaranteesMainDealConditions,
  type TGuaranteesMainDealConditionsValues,
  type TPatchGuaranteesMainDealConditionsPayload,
} from '~/store/guarantees/types.ts';
import { utils } from '../../components/root/shared/utils.ts';
import {
  MAIN_DEAL_CONDITIONS_CONTRACT_NAME_OPTIONS,
  MAIN_DEAL_CONDITIONS_REQUIRED_FIELDS,
  MAIN_DEAL_CONDITIONS_RISK_APPROVAL_STATUS_OPTIONS,
  MAIN_DEAL_CONDITIONS_STATUS_OPTIONS,
  MANAGER_MAIN_DEAL_CONDITIONS_TEXT,
} from './constants.ts';
import {
  buildManagerMainDealConditionsPayload,
  getManagerMainDealConditionsInitialValues,
  managerMainDealConditionsResolver,
} from './mainDealConditionsUtils.ts';

type TManagerMainDealConditionsSectionProps = {
  isLoading: boolean;
  isSaving: boolean;
  mainDealConditions: TGuaranteesMainDealConditions | null;
  onDirtyChange: (isDirty: boolean) => void;
  onSubmit: (payload: TPatchGuaranteesMainDealConditionsPayload) => Promise<boolean>;
};

const ManagerMainDealConditionsSection: React.FC<TManagerMainDealConditionsSectionProps> = ({
  isLoading,
  isSaving,
  mainDealConditions,
  onDirtyChange,
  onSubmit,
}) => {
  const defaultValues = React.useMemo(
    () => getManagerMainDealConditionsInitialValues(mainDealConditions),
    [mainDealConditions]
  );
  const formMethods = useForm<TGuaranteesMainDealConditionsValues>({
    defaultValues,
    resolver: managerMainDealConditionsResolver,
    mode: 'onSubmit',
  });
  const {
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isDirty },
  } = formMethods;
  const { isActionDisabled } = utils({
    defaultValues,
    isDirty,
    isDisabled: isSaving,
    onDirtyChange,
    requireChangesToEnableActions: true,
    reset,
  });

  const touchAndValidateFields = React.useCallback(
    (fieldNames: readonly (keyof TGuaranteesMainDealConditionsValues)[]) => {
      fieldNames.forEach((fieldName) => {
        const fieldPath = fieldName as FieldPath<TGuaranteesMainDealConditionsValues>;

        setValue(fieldPath, getValues(fieldPath), {
          shouldDirty: false,
          shouldTouch: true,
          shouldValidate: false,
        });
      });

      void trigger(fieldNames as FieldPath<TGuaranteesMainDealConditionsValues>[]);
    },
    [getValues, setValue, trigger]
  );

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      touchAndValidateFields(MAIN_DEAL_CONDITIONS_REQUIRED_FIELDS);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [defaultValues, touchAndValidateFields]);

  const handleMainDealConditionsSubmit: SubmitHandler<
    TGuaranteesMainDealConditionsValues
  > = async (values) => {
    const isSaved = await onSubmit(buildManagerMainDealConditionsPayload(values));

    if (isSaved) {
      reset(values);
    }
  };

  return (
    <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
      <Typography sx={guaranteesClientPageStyles.flowSectionTitle}>
        {MANAGER_MAIN_DEAL_CONDITIONS_TEXT.sectionTitle}
      </Typography>

      {isLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {MANAGER_MAIN_DEAL_CONDITIONS_TEXT.loadingText}
          </Typography>
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={(event) => {
              void handleSubmit(handleMainDealConditionsSubmit)(event);
            }}
          >
            <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
              <FormInput
                name={GuaranteesMainDealConditionsFieldName.BusinessCategory}
                label={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.businessCategoryLabel}
                disabled
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormAutocomplete
                name={GuaranteesMainDealConditionsFieldName.RiskApprovalStatus}
                label={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.riskApprovalStatusLabel}
                placeholder={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.riskApprovalStatusPlaceholder}
                options={MAIN_DEAL_CONDITIONS_RISK_APPROVAL_STATUS_OPTIONS}
                disabled={isSaving}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormAutocomplete
                name={GuaranteesMainDealConditionsFieldName.ContractName}
                label={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.contractNameLabel}
                placeholder={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.contractNamePlaceholder}
                options={MAIN_DEAL_CONDITIONS_CONTRACT_NAME_OPTIONS}
                disabled={isSaving}
                sx={guaranteesClientPageStyles.flowLongAutocompleteField}
              />
              <FormInput
                name={GuaranteesMainDealConditionsFieldName.MaxGuaranteeTermDays}
                label={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.maxGuaranteeTermDaysLabel}
                disabled={isSaving}
                type="number"
                inputProps={{
                  min: 1,
                  step: 1,
                }}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormAutocomplete
                name={GuaranteesMainDealConditionsFieldName.MainDealConditionsStatus}
                label={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.mainDealConditionsStatusLabel}
                placeholder={
                  MANAGER_MAIN_DEAL_CONDITIONS_TEXT.mainDealConditionsStatusPlaceholder
                }
                options={MAIN_DEAL_CONDITIONS_STATUS_OPTIONS}
                disabled={isSaving}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesMainDealConditionsFieldName.NormativeCoverageRatio}
                label={MANAGER_MAIN_DEAL_CONDITIONS_TEXT.normativeCoverageRatioLabel}
                disabled={isSaving}
                sx={guaranteesClientPageStyles.flowField}
              />
            </Box>

            <Box sx={guaranteesClientPageStyles.flowActions}>
              <Button
                type="submit"
                variant="contained"
                disabled={isActionDisabled}
                startIcon={isSaving ? <ButtonLoadingIcon /> : undefined}
                sx={dataFormStyles.documentPrimaryAction}
              >
                {MANAGER_MAIN_DEAL_CONDITIONS_TEXT.saveButton}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      )}
    </Box>
  );
};

export default ManagerMainDealConditionsSection;
